import React from 'react';
import moment, { Moment } from 'moment';
import 'moment/locale/fr';
import { IoAdd } from 'react-icons/io5';
import './NewAppointment.scss';
import useNewAppointmentVM, { DayChoice, TimeChoice } from './useNewAppointmentVM';
import { Loader } from 'components/Loader/Loader';
import { UserPageHeader } from 'components/UserPageHeader/UserPageHeader';
import { UserCard } from 'components/UserCard/UserCard';
import { StatusBadge } from 'components/StatusBadge/StatusBadge';
import appointmentAskIcon from 'assets/images/icons/users/menu/appointment-ask.svg';
import calendarCheckIcon from 'assets/images/icons/users/calendar-check.svg';
import calendarIcon from 'assets/images/icons/users/calendar.svg';
import clockIcon from 'assets/images/icons/users/clock.svg';
import resumeIcon from 'assets/images/icons/users/resume.svg';
import editIcon from 'assets/images/icons/users/edit.svg';
import plusIcon from 'assets/images/icons/users/plus.svg';
import checkIcon from 'assets/images/icons/users/check.svg';
import crossIcon from 'assets/images/icons/users/cross.svg';
import arrowLeftIcon from 'assets/images/icons/users/arrow-left.svg';
import arrowRightIcon from 'assets/images/icons/users/arrow-right.svg';
import arrowDownIcon from 'assets/images/icons/users/arrow-down.svg';

moment.locale('fr');

const DAY_IN_WORDS: Record<number, string> = {
    1: 'premier',
    2: 'deux',
    3: 'trois',
    4: 'quatre',
    5: 'cinq',
    6: 'six',
    7: 'sept',
    8: 'huit',
    9: 'neuf',
    10: 'dix',
    11: 'onze',
    12: 'douze',
    13: 'treize',
    14: 'quatorze',
    15: 'quinze',
    16: 'seize',
    17: 'dix-sept',
    18: 'dix-huit',
    19: 'dix-neuf',
    20: 'vingt',
    21: 'vingt-et-un',
    22: 'vingt-deux',
    23: 'vingt-trois',
    24: 'vingt-quatre',
    25: 'vingt-cinq',
    26: 'vingt-six',
    27: 'vingt-sept',
    28: 'vingt-huit',
    29: 'vingt-neuf',
    30: 'trente',
    31: 'trente-et-un'
};

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatDateLong = (d: Moment) => {
    const weekday = cap(d.format('dddd'));
    const day = DAY_IN_WORDS[d.date()];
    const month = d.format('MMMM');
    const year = d.format('YYYY');
    return `${weekday} ${day} ${month} ${year}`;
};

type VM = ReturnType<typeof useNewAppointmentVM>;

export const NewAppointment: React.FC = () => {
    const vm = useNewAppointmentVM();

    if (vm.isLoading)
        return (
            <main id="new-appointment">
                <Loader />
            </main>
        );

    return (
        <main id="new-appointment">
            <UserPageHeader icon={<img src={appointmentAskIcon} alt="" />} title="Demander un rendez-vous" />

            {vm.step === 'list' && <ListView vm={vm} />}
            {vm.step === 'day' && <StepDay vm={vm} />}
            {vm.step === 'calendar' && <StepCalendar vm={vm} />}
            {vm.step === 'time' && <StepTime vm={vm} />}
            {vm.step === 'summary' && <StepSummary vm={vm} />}
        </main>
    );
};

const ListView: React.FC<{ vm: VM }> = ({ vm }) => (
    <UserCard icon={<img src={calendarCheckIcon} alt="" />} title="Mes demandes de rendez-vous">
        {vm.upcoming.length === 0 ? (
            <p className="new-appointment__empty">Il n'y a pas de rendez-vous prévu prochainement.</p>
        ) : (
            <ul className="new-appointment__upcoming-list">
                {vm.upcoming.map((a) => {
                    const start = moment(a.started_at);
                    const slotText = start.hour() < 12 ? 'Le matin' : "L'après-midi";
                    return (
                        <li key={a.id} className="new-appointment__upcoming-item">
                            {a.status && <StatusBadge status={a.status} />}
                            <strong>{a.label}</strong>
                            <span>{cap(formatDateLong(start))}</span>
                            <span>{slotText}</span>
                        </li>
                    );
                })}
            </ul>
        )}
        <div className="new-appointment__cta-group">
            <button type="button" className="new-appointment__cta" onClick={() => vm.startFlow('appointment')}>
                <img src={plusIcon} alt="" aria-hidden="true" />
                <span>Demander un nouveau rendez-vous</span>
            </button>
            <button type="button" className="new-appointment__cta new-appointment__cta--exit" onClick={() => vm.startFlow('exit')}>
                <IoAdd size={26} aria-hidden="true" />
                <span>Demander une sortie du dispositif</span>
            </button>
        </div>
    </UserCard>
);

const StepShell: React.FC<{
    icon: React.ReactNode;
    title: string;
    onBack: () => void;
    onCancel: () => void;
    children: React.ReactNode;
    primary?: React.ReactNode;
}> = ({ icon, title, onBack, onCancel, children, primary }) => (
    <UserCard icon={icon} title={title}>
        <div className="new-appointment__step">
            <div className="new-appointment__step-actions">
                <button type="button" className="action-pill action-pill--purple" onClick={onBack}>
                    <img src={arrowLeftIcon} alt="" aria-hidden="true" /> <span>Revenir en arrière</span>
                </button>
                <button type="button" className="action-pill action-pill--pink" onClick={onCancel}>
                    <img src={crossIcon} alt="" aria-hidden="true" /> <span>Annuler ma demande</span>
                </button>
            </div>
            <div className="new-appointment__step-content">{children}</div>
            {primary && <div className="new-appointment__step-primary">{primary}</div>}
        </div>
    </UserCard>
);

const StepDay: React.FC<{ vm: VM }> = ({ vm }) => (
    <StepShell
        icon={<img src={calendarIcon} alt="" />}
        title={`Etape ${vm.stepNumber} : Choisir le jour du rendez-vous`}
        onBack={vm.goBack}
        onCancel={vm.cancelFlow}
        primary={
            <button type="button" className="primary-pill primary-pill--green" onClick={vm.goNext}>
                <img src={checkIcon} alt="" aria-hidden="true" /> <span>Valider mon choix</span>
            </button>
        }
    >
        <p className="new-appointment__question">Quel jour ?</p>
        <SelectPill
            value={vm.dayChoice}
            onChange={(v) => vm.setDayChoice(v as DayChoice)}
            options={[
                { value: 'today', label: "Aujourd'hui" },
                { value: 'tomorrow', label: 'Demain' },
                { value: 'other', label: 'Autre jour' }
            ]}
        />
    </StepShell>
);

const StepCalendar: React.FC<{ vm: VM }> = ({ vm }) => {
    const weekStart = moment().add(vm.weekOffset, 'week').startOf('isoWeek');
    const weekEnd = weekStart.clone().add(6, 'day');
    const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'day'));
    const today = moment().startOf('day');

    const periodLabel =
        vm.weekOffset === 0
            ? 'Cette semaine'
            : vm.weekOffset > 0
              ? `Dans ${vm.weekOffset} semaine${vm.weekOffset > 1 ? 's' : ''}`
              : `Il y a ${-vm.weekOffset} semaine${-vm.weekOffset > 1 ? 's' : ''}`;

    const monthsLabel =
        weekStart.month() === weekEnd.month()
            ? cap(weekStart.format('MMMM YYYY'))
            : `${cap(weekStart.format('MMMM'))} - ${cap(weekEnd.format('MMMM YYYY'))}`;

    const isAvailable = (d: Moment) => d.isSameOrAfter(today, 'day');

    return (
        <StepShell
            icon={<img src={calendarIcon} alt="" />}
            title={`Etape ${vm.stepNumber} : Calendrier`}
            onBack={vm.goBack}
            onCancel={vm.cancelFlow}
            primary={
                <button type="button" className="primary-pill primary-pill--green" onClick={vm.goNext} disabled={!vm.selectedDate}>
                    <img src={checkIcon} alt="" aria-hidden="true" /> Valider mon choix
                </button>
            }
        >
            <div className="new-appointment__week-nav">
                <button type="button" className="action-pill action-pill--purple" onClick={() => vm.setWeekOffset(vm.weekOffset - 1)}>
                    <img src={arrowLeftIcon} alt="" aria-hidden="true" /> <span>Avant</span>
                </button>
                <div className="new-appointment__week-period">
                    <strong>{periodLabel}</strong>
                    <span>{monthsLabel}</span>
                </div>
                <button type="button" className="action-pill action-pill--purple" onClick={() => vm.setWeekOffset(vm.weekOffset + 1)}>
                    <span>Après</span> <img src={arrowRightIcon} alt="" aria-hidden="true" />
                </button>
            </div>

            <div className="new-appointment__week-grid">
                {weekDays.map((d) => {
                    const available = isAvailable(d);
                    const selected = vm.selectedDate?.isSame(d, 'day') ?? false;
                    return (
                        <div
                            key={d.toISOString()}
                            className={`day-card ${available ? '' : 'day-card--unavailable'} ${selected ? 'day-card--selected' : ''}`}
                        >
                            <div className="day-card__weekday">{d.format('dddd').toUpperCase()}</div>
                            <div className="day-card__num">{d.date()}</div>
                            <div className="day-card__day-text">{cap(DAY_IN_WORDS[d.date()])}</div>
                            <div className="day-card__month">{cap(d.format('MMMM'))}</div>
                            {available ? (
                                <button type="button" className="day-card__choose" onClick={() => vm.setSelectedDate(d.clone())}>
                                    {selected ? 'Choisi' : 'Choisir'}
                                </button>
                            ) : (
                                <div className="day-card__unavailable">Indisponible</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </StepShell>
    );
};

const StepTime: React.FC<{ vm: VM }> = ({ vm }) => (
    <StepShell
        icon={<img src={clockIcon} alt="" />}
        title={`Etape ${vm.stepNumber} : Choisir l'heure du rendez-vous`}
        onBack={vm.goBack}
        onCancel={vm.cancelFlow}
        primary={
            <button type="button" className="primary-pill primary-pill--green" onClick={vm.goNext}>
                <img src={checkIcon} alt="" aria-hidden="true" /> Valider mon choix
            </button>
        }
    >
        <p className="new-appointment__chosen-date">
            J'ai choisi la date : <strong>{vm.resolvedDate ? formatDateLong(vm.resolvedDate) : ''}.</strong>
        </p>
        <p className="new-appointment__question">Quelle heure ?</p>
        <SelectPill
            value={vm.timeChoice}
            onChange={(v) => vm.setTimeChoice(v as TimeChoice)}
            options={[
                { value: 'morning', label: 'Matin' },
                { value: 'afternoon', label: 'Après-midi' }
            ]}
        />
    </StepShell>
);

const StepSummary: React.FC<{ vm: VM }> = ({ vm }) => {
    const dateText = vm.resolvedDate ? formatDateLong(vm.resolvedDate) : '';
    const timeText = vm.timeChoice === 'morning' ? 'Le matin' : "L'après-midi";
    const days = vm.resolvedDate ? vm.resolvedDate.diff(moment().startOf('day'), 'days') : 0;
    const isImmediate = days <= 1;
    const whenText = days === 0 ? "aujourd'hui" : days === 1 ? 'demain' : `dans ${days} jours`;
    const isExit = vm.requestType === 'exit';
    const requestLabel = isExit ? 'demande de sortie du dispositif' : 'demande de rendez-vous';
    const canConfirm = !isExit || vm.exitReason.trim().length > 0;

    return (
        <StepShell
            icon={<img src={resumeIcon} alt="" />}
            title={`Etape ${vm.stepNumber} : Résumé de la demande`}
            onBack={vm.goBack}
            onCancel={vm.cancelFlow}
            primary={
                <div className="new-appointment__summary-actions">
                    <button type="button" className="primary-pill primary-pill--blue" onClick={vm.goBack}>
                        <img src={editIcon} alt="" aria-hidden="true" /> Modifier ma {requestLabel}
                    </button>
                    <button type="button" className="primary-pill primary-pill--green" onClick={vm.submit} disabled={!canConfirm}>
                        <img src={checkIcon} alt="" aria-hidden="true" /> Confirmer ma {requestLabel}
                    </button>
                </div>
            }
        >
            <div className="new-appointment__summary-wrap">
                <ul className="new-appointment__summary">
                    <li>J'ai fait une {requestLabel}.</li>
                    <li>
                        <img src={calendarIcon} alt="" aria-hidden="true" />
                        <span>
                            J'ai choisi la date : <strong>{dateText}.</strong>
                        </span>
                    </li>
                    <li>
                        <img src={clockIcon} alt="" aria-hidden="true" />
                        <span>
                            J'ai choisi l'heure : <strong>{timeText}.</strong>
                        </span>
                    </li>
                    <li>
                        <strong>Mon rendez-vous est{isImmediate ? '' : ' dans'} {whenText}.</strong>
                    </li>
                </ul>

                {isExit && (
                    <div className="new-appointment__exit-reason">
                        <label htmlFor="exit-reason">Pourquoi souhaitez-vous sortir du dispositif ?</label>
                        <textarea
                            id="exit-reason"
                            value={vm.exitReason}
                            onChange={(e) => vm.setExitReason(e.target.value)}
                            placeholder="Précisez la raison de votre demande de sortie…"
                            rows={4}
                        />
                    </div>
                )}
            </div>
        </StepShell>
    );
};

const SelectPill: React.FC<{
    value: string;
    onChange: (v: string) => void;
    options: Array<{ value: string; label: string }>;
}> = ({ value, onChange, options }) => (
    <div className="new-appointment__select-wrap">
        <select className="new-appointment__select" value={value} onChange={(e) => onChange(e.target.value)}>
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
        <img src={arrowDownIcon} alt="" aria-hidden="true" />
    </div>
);
